import React, { useState } from 'react'
import { 
  Monitor, 
  Tablet, 
  Smartphone, 
  Eye, 
  Code,
  Palette,
  Settings,
  CheckCircle,
  AlertCircle,
  Info,
  Maximize2,
  Minimize2,
  RotateCcw,
  Grid,
  AlignCenter,
  AlignLeft,
  AlignRight,
  Type,
  Image,
  Layout,
  Layers
} from 'lucide-react'

interface ResponsiveSettings {
  breakpoints: {
    mobile: number
    tablet: number
    desktop: number
  }
  mobileSettings: {
    fontSize: number
    spacing: number
    hideElements: string[]
    stackElements: string[]
  }
  tabletSettings: {
    fontSize: number
    spacing: number
    hideElements: string[]
    stackElements: string[]
  }
  desktopSettings: {
    fontSize: number
    spacing: number
    hideElements: string[]
    stackElements: string[]
  }
}

interface ResponsivePanelProps {
  responsiveSettings: ResponsiveSettings
  onUpdate: (settings: ResponsiveSettings) => void
  previewMode: 'desktop' | 'tablet' | 'mobile'
  onPreviewModeChange: (mode: 'desktop' | 'tablet' | 'mobile') => void
  websiteComponents: any[]
  onComponentUpdate: (componentId: string, updates: any) => void
}

export default function ResponsivePanel({ 
  responsiveSettings, 
  onUpdate, 
  previewMode,
  onPreviewModeChange,
  websiteComponents,
  onComponentUpdate
}: ResponsivePanelProps) {
  const [activeTab, setActiveTab] = useState<'breakpoints' | 'mobile' | 'tablet' | 'desktop' | 'components'>('breakpoints')

  const handleSettingsUpdate = (device: 'mobile' | 'tablet' | 'desktop', field: string, value: any) => {
    onUpdate({
      ...responsiveSettings,
      [`${device}Settings`]: {
        ...responsiveSettings[`${device}Settings`],
        [field]: value
      }
    })
  }

  const handleBreakpointUpdate = (breakpoint: string, value: number) => {
    onUpdate({
      ...responsiveSettings,
      breakpoints: {
        ...responsiveSettings.breakpoints,
        [breakpoint]: value
      }
    })
  }

  const generateResponsiveCSS = () => {
    const { breakpoints, mobileSettings, tabletSettings, desktopSettings } = responsiveSettings

    return `
/* Mobile First Approach */
@media (max-width: ${breakpoints.mobile}px) {
  body {
    font-size: ${mobileSettings.fontSize}px;
    line-height: 1.6;
  }
  
  .container {
    padding: ${mobileSettings.spacing}px;
  }
  
  ${mobileSettings.hideElements.map(element => `
  .${element} {
    display: none !important;
  }
  `).join('')}
  
  ${mobileSettings.stackElements.map(element => `
  .${element} {
    flex-direction: column !important;
    width: 100% !important;
  }
  `).join('')}
}

/* Tablet Styles */
@media (min-width: ${breakpoints.mobile + 1}px) and (max-width: ${breakpoints.tablet}px) {
  body {
    font-size: ${tabletSettings.fontSize}px;
    line-height: 1.6;
  }
  
  .container {
    padding: ${tabletSettings.spacing}px;
  }
  
  ${tabletSettings.hideElements.map(element => `
  .${element} {
    display: none !important;
  }
  `).join('')}
  
  ${tabletSettings.stackElements.map(element => `
  .${element} {
    flex-direction: column !important;
  }
  `).join('')}
}

/* Desktop Styles */
@media (min-width: ${breakpoints.tablet + 1}px) {
  body {
    font-size: ${desktopSettings.fontSize}px;
    line-height: 1.6;
  }
  
  .container {
    padding: ${desktopSettings.spacing}px;
  }
  
  ${desktopSettings.hideElements.map(element => `
  .${element} {
    display: none !important;
  }
  `).join('')}
}
    `.trim()
  }

  const getDeviceIcon = (device: string) => {
    switch (device) {
      case 'mobile': return <Smartphone className="w-5 h-5" />
      case 'tablet': return <Tablet className="w-5 h-5" />
      case 'desktop': return <Monitor className="w-5 h-5" />
      default: return <Monitor className="w-5 h-5" />
    }
  }

  const getDeviceColor = (device: string) => {
    switch (device) {
      case 'mobile': return 'text-blue-600'
      case 'tablet': return 'text-purple-600'
      case 'desktop': return 'text-green-600'
      default: return 'text-gray-600'
    }
  }

  const renderBreakpointsTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Responsive Breakpoints</h4>
            <p className="text-blue-800 text-sm">
              Set the pixel values where your website layout changes for different devices.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Mobile Breakpoint */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <Smartphone className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <div className="font-medium text-gray-900">Mobile</div>
              <div className="text-sm text-gray-600">Portrait phones and small devices</div>
            </div>
          </div>
          <div className="flex items-center">
            <input
              type="number"
              value={responsiveSettings.breakpoints.mobile}
              onChange={(e) => handleBreakpointUpdate('mobile', parseInt(e.target.value) || 0)}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-right"
            />
            <span className="ml-2 text-sm text-gray-600">px</span>
          </div>
        </div>

        {/* Tablet Breakpoint */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <Tablet className="w-6 h-6 text-purple-600 mr-3" />
            <div>
              <div className="font-medium text-gray-900">Tablet</div>
              <div className="text-sm text-gray-600">Landscape phones and tablets</div>
            </div>
          </div>
          <div className="flex items-center">
            <input
              type="number"
              value={responsiveSettings.breakpoints.tablet}
              onChange={(e) => handleBreakpointUpdate('tablet', parseInt(e.target.value) || 0)}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-right"
            />
            <span className="ml-2 text-sm text-gray-600">px</span>
          </div>
        </div>

        {/* Desktop Breakpoint */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <Monitor className="w-6 h-6 text-green-600 mr-3" />
            <div>
              <div className="font-medium text-gray-900">Desktop</div>
              <div className="text-sm text-gray-600">Laptops and desktop computers</div>
            </div>
          </div>
          <div className="flex items-center">
            <input
              type="number"
              value={responsiveSettings.breakpoints.desktop}
              onChange={(e) => handleBreakpointUpdate('desktop', parseInt(e.target.value) || 0)}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-right"
            />
            <span className="ml-2 text-sm text-gray-600">px</span>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Current Breakpoints</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-blue-600">Mobile:</span>
            <span>0 - {responsiveSettings.breakpoints.mobile}px</span>
          </div>
          <div className="flex justify-between">
            <span className="text-purple-600">Tablet:</span>
            <span>{responsiveSettings.breakpoints.mobile + 1} - {responsiveSettings.breakpoints.tablet}px</span>
          </div>
          <div className="flex justify-between">
            <span className="text-green-600">Desktop:</span>
            <span>{responsiveSettings.breakpoints.tablet + 1}px+</span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderDeviceTab = (device: 'mobile' | 'tablet' | 'desktop') => {
    const settings = responsiveSettings[`${device}Settings`]
    const deviceName = device.charAt(0).toUpperCase() + device.slice(1)

    return (
      <div className="space-y-6">
        <div className="flex items-center mb-4">
          {getDeviceIcon(device)}
          <h3 className="text-lg font-semibold text-gray-900 ml-2">{deviceName} Settings</h3>
        </div>

        {/* Font Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Base Font Size
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="12"
              max="24"
              value={settings.fontSize}
              onChange={(e) => handleSettingsUpdate(device, 'fontSize', parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm font-medium text-gray-600 w-12">
              {settings.fontSize}px
            </span>
          </div>
        </div>

        {/* Spacing */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Container Spacing
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="0"
              max="60"
              value={settings.spacing}
              onChange={(e) => handleSettingsUpdate(device, 'spacing', parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm font-medium text-gray-600 w-12">
              {settings.spacing}px
            </span>
          </div>
        </div>

        {/* Hide Elements */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hide Elements
          </label>
          <div className="space-y-2">
            {websiteComponents.map((component) => (
              <label key={component.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.hideElements.includes(component.id)}
                  onChange={(e) => {
                    const newHideElements = e.target.checked
                      ? [...settings.hideElements, component.id]
                      : settings.hideElements.filter(id => id !== component.id)
                    handleSettingsUpdate(device, 'hideElements', newHideElements)
                  }}
                  className="mr-3"
                />
                <span className="text-sm text-gray-700">
                  {component.type} - {component.id}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Stack Elements */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stack Elements (Vertical Layout)
          </label>
          <div className="space-y-2">
            {websiteComponents.map((component) => (
              <label key={component.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.stackElements.includes(component.id)}
                  onChange={(e) => {
                    const newStackElements = e.target.checked
                      ? [...settings.stackElements, component.id]
                      : settings.stackElements.filter(id => id !== component.id)
                    handleSettingsUpdate(device, 'stackElements', newStackElements)
                  }}
                  className="mr-3"
                />
                <span className="text-sm text-gray-700">
                  {component.type} - {component.id}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderComponentsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Component Settings</h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Preview:</span>
          <button
            onClick={() => onPreviewModeChange('mobile')}
            className={`p-2 rounded-lg ${previewMode === 'mobile' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
          >
            <Smartphone className="w-4 h-4" />
          </button>
          <button
            onClick={() => onPreviewModeChange('tablet')}
            className={`p-2 rounded-lg ${previewMode === 'tablet' ? 'bg-purple-100 text-purple-600' : 'text-gray-600'}`}
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            onClick={() => onPreviewModeChange('desktop')}
            className={`p-2 rounded-lg ${previewMode === 'desktop' ? 'bg-green-100 text-green-600' : 'text-gray-600'}`}
          >
            <Monitor className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {websiteComponents.map((component) => (
          <div key={component.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Layers className="w-5 h-5 text-gray-600 mr-2" />
                <span className="font-medium text-gray-900">{component.type}</span>
                <span className="ml-2 text-sm text-gray-500">#{component.id}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onComponentUpdate(component.id, { visible: !component.visible })}
                  className={`p-1 rounded ${component.visible ? 'text-green-600' : 'text-red-600'}`}
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Width
                </label>
                <input
                  type="text"
                  value={component.style?.width || ''}
                  onChange={(e) => onComponentUpdate(component.id, { 
                    style: { ...component.style, width: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="100%"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Margin
                </label>
                <input
                  type="text"
                  value={component.style?.margin || ''}
                  onChange={(e) => onComponentUpdate(component.id, { 
                    style: { ...component.style, margin: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="0 auto"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Padding
                </label>
                <input
                  type="text"
                  value={component.style?.padding || ''}
                  onChange={(e) => onComponentUpdate(component.id, { 
                    style: { ...component.style, padding: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="20px"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const tabs = [
    { id: 'breakpoints', label: 'Breakpoints', icon: <Grid className="w-4 h-4" /> },
    { id: 'mobile', label: 'Mobile', icon: <Smartphone className="w-4 h-4" /> },
    { id: 'tablet', label: 'Tablet', icon: <Tablet className="w-4 h-4" /> },
    { id: 'desktop', label: 'Desktop', icon: <Monitor className="w-4 h-4" /> },
    { id: 'components', label: 'Components', icon: <Layers className="w-4 h-4" /> }
  ]

  return (
    <div className="bg-white rounded-2xl shadow-lg">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Responsive Design</h2>
          <div className="flex items-center space-x-2">
            <button className="p-2 text-gray-600 hover:text-gray-900">
              <Code className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-600 hover:text-gray-900">
              <Eye className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'breakpoints' && renderBreakpointsTab()}
        {activeTab === 'mobile' && renderDeviceTab('mobile')}
        {activeTab === 'tablet' && renderDeviceTab('tablet')}
        {activeTab === 'desktop' && renderDeviceTab('desktop')}
        {activeTab === 'components' && renderComponentsTab()}
      </div>

      {/* Generated CSS */}
      <div className="border-t border-gray-200 p-6 bg-gray-50">
        <h3 className="font-medium text-gray-900 mb-3">Generated Responsive CSS</h3>
        <div className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs whitespace-pre-wrap">
            {generateResponsiveCSS()}
          </pre>
        </div>
      </div>
    </div>
  )
}
